import React, { useEffect, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Bold, Italic, Strikethrough, List, ListOrdered, Quote, Link2, Unlink } from 'lucide-react'
import * as S from './style'

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const lastEmittedValue = useRef(value)
  const [isFocused, setIsFocused] = useState(false)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || '',
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? '' : editor.getHTML()
      lastEmittedValue.current = html
      onChange(html)
    },
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  })

  useEffect(() => {
    if (!editor) return
    if (value === lastEmittedValue.current) return
    lastEmittedValue.current = value || ''
    editor.commands.setContent(value || '', false)
  }, [value, editor])

  useEffect(() => {
    return () => {
      editor?.destroy()
    }
  }, [editor])

  if (!editor) return null

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Endereço do link:', previousUrl || 'https://')
    if (url === null) return
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run()
  }

  return (
    <S.Wrapper $focused={isFocused}>
      <S.Toolbar>
        <S.ToolbarButton
          type="button"
          title="Negrito"
          $active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={15} />
        </S.ToolbarButton>
        <S.ToolbarButton
          type="button"
          title="Itálico"
          $active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={15} />
        </S.ToolbarButton>
        <S.ToolbarButton
          type="button"
          title="Tachado"
          $active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough size={15} />
        </S.ToolbarButton>

        <S.ToolbarDivider />

        <S.ToolbarButton
          type="button"
          title="Lista com marcadores"
          $active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={15} />
        </S.ToolbarButton>
        <S.ToolbarButton
          type="button"
          title="Lista numerada"
          $active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={15} />
        </S.ToolbarButton>
        <S.ToolbarButton
          type="button"
          title="Citação"
          $active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote size={15} />
        </S.ToolbarButton>

        <S.ToolbarDivider />

        <S.ToolbarButton
          type="button"
          title="Inserir link"
          $active={editor.isActive('link')}
          onClick={setLink}
        >
          <Link2 size={15} />
        </S.ToolbarButton>
        <S.ToolbarButton
          type="button"
          title="Remover link"
          disabled={!editor.isActive('link')}
          onClick={() => editor.chain().focus().unsetLink().run()}
        >
          <Unlink size={15} />
        </S.ToolbarButton>
      </S.Toolbar>
      <S.EditorArea onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor} />
      </S.EditorArea>
    </S.Wrapper>
  )
}

export default RichTextEditor
